import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { useAuth } from "../../auth/AuthProvider";
import { api } from "../../lib/api";
import { Alert, Button, Card, CardHeader, Field, Input, Mono } from "../../components/ui";

interface EnrollResult {
  secret: string;
  otpauthUri: string;
  recoveryCodes: string[];
}

type Phase =
  | { step: "idle" }
  | { step: "password" }
  | { step: "setup"; otpauthUri: string; recoveryCodes: string[]; secret: string }
  | { step: "success" }
  | { step: "disable" };

/* Three keys, not three words: `settings.json` holds both halves of each. */
const STEPS = ["password", "scan", "verify"] as const;

export function TwoFactorSection() {
  const { t } = useTranslation("settings");
  const { user, reloadSession } = useAuth();
  const [phase, setPhase] = useState<Phase>({ step: "idle" });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [codesSaved, setCodesSaved] = useState(false);

  const totpEnabled = user?.totpEnabled ?? false;

  useEffect(() => {
    let cancelled = false;
    if (phase.step !== "setup") return;
    setQrDataUrl(null);
    void QRCode.toDataURL(phase.otpauthUri, { width: 220, margin: 1 }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  async function beginEnrollment(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api<EnrollResult>("/v1/auth/2fa/enroll", {
        method: "POST",
        body: { password },
      });
      setPhase({
        step: "setup",
        otpauthUri: result.otpauthUri,
        recoveryCodes: result.recoveryCodes,
        secret: result.secret,
      });
      setCodesSaved(false);
      setCopied(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("twoFactor.error.enroll"));
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmEnrollment(event: FormEvent) {
    event.preventDefault();
    if (phase.step !== "setup") return;
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/2fa/confirm", { method: "POST", body: { code } });
      setPhase({ step: "success" });
      setPassword("");
      setCode("");
      setTimeout(async () => {
        await reloadSession();
        setPhase({ step: "idle" });
      }, 2000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("twoFactor.error.code"));
    } finally {
      setSubmitting(false);
    }
  }

  async function disable(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/2fa/disable", { method: "POST", body: { password, code } });
      setPhase({ step: "idle" });
      setPassword("");
      setCode("");
      await reloadSession();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("twoFactor.error.disable"));
    } finally {
      setSubmitting(false);
    }
  }

  async function copyCodes() {
    if (phase.step !== "setup") return;
    await navigator.clipboard.writeText(phase.recoveryCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadCodes() {
    if (phase.step !== "setup") return;
    const rule = "=".repeat(50);
    const content = [
      t("twoFactor.file.title"),
      rule,
      "",
      phase.recoveryCodes.join("\n"),
      "",
      rule,
      t("twoFactor.file.note"),
      "",
      t("twoFactor.file.generated", { at: new Date().toISOString() }),
      "",
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `maxlayer-recovery-codes-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const currentStep = phase.step === "password" ? 0 : phase.step === "setup" ? 1 : -1;

  return (
    <Card>
      <CardHeader
        title={t("twoFactor.title")}
        description={totpEnabled ? t("twoFactor.on") : t("twoFactor.off")}
      />

      <div className="p-5">
        {/* Step indicator */}
        {currentStep >= 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {STEPS.map((step, i) => (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                        i < currentStep
                          ? "bg-accent text-white"
                          : i === currentStep
                          ? "bg-accent text-white"
                          : "bg-line text-fg-subtle"
                      }`}
                    >
                      {i < currentStep ? "✓" : i + 1}
                    </div>
                    <div className="mt-2 text-center">
                      <p className="text-xs font-medium text-fg">
                        {t(`twoFactor.steps.${step}` as "twoFactor.steps.scan")}
                      </p>
                      <p className="text-xs text-fg-subtle">
                        {t(`twoFactor.steps.${step}Hint` as "twoFactor.steps.scanHint")}
                      </p>
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`mx-2 h-0.5 w-12 transition-colors ${
                        i < currentStep ? "bg-accent" : "bg-line"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Idle state */}
        {phase.step === "idle" && (
          <div className="space-y-4">
            {totpEnabled ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg bg-mint/10 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mint/20">
                    <svg className="h-5 w-5 text-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-fg">{t("twoFactor.enabled")}</p>
                    <p className="text-sm text-fg-subtle">{t("twoFactor.enabledLine")}</p>
                  </div>
                </div>
                <Button variant="secondary" onClick={() => setPhase({ step: "disable" })}>
                  {t("twoFactor.disable")}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-accent/5 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                    <svg className="h-5 w-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-fg">{t("twoFactor.protect")}</p>
                    <p className="text-sm text-fg-subtle">{t("twoFactor.protectLine")}</p>
                  </div>
                </div>
                <Button onClick={() => setPhase({ step: "password" })}>
                  {t("twoFactor.enable")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Password phase */}
        {phase.step === "password" && (
          <form onSubmit={beginEnrollment} className="space-y-4">
            {error && <Alert>{error}</Alert>}
            <div className="space-y-2">
              <p className="text-sm font-medium text-fg">{t("twoFactor.confirmPassword")}</p>
              <p className="text-sm text-fg-subtle">{t("twoFactor.confirmPasswordLine")}</p>
            </div>
            <Field label={t("twoFactor.password")} htmlFor="enroll-password">
              <Input
                id="enroll-password"
                type="password"
                autoComplete="current-password"
                required
                autoFocus
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-3">
              <Button type="submit" loading={submitting}>
                {t("twoFactor.continue")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setPhase({ step: "idle" });
                  setPassword("");
                  setError(null);
                }}
              >
                {t("twoFactor.cancel")}
              </Button>
            </div>
          </form>
        )}

        {/* Setup phase */}
        {phase.step === "setup" && (
          <div className="space-y-6">
            {error && <Alert>{error}</Alert>}

            {/* QR Code section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-fg">{t("twoFactor.scanTitle")}</p>
                <p className="text-sm text-fg-subtle">{t("twoFactor.scanLine")}</p>
              </div>
              <div className="flex flex-col items-center gap-4 rounded-lg bg-canvas p-4 sm:p-6">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt={t("twoFactor.qrAlt")}
                    className="aspect-square w-full max-w-56"
                  />
                ) : (
                  <div className="aspect-square w-full max-w-56 animate-pulse rounded-lg bg-line" />
                )}
                <div className="w-full space-y-2">
                  <p className="text-xs font-medium text-fg-subtle">{t("twoFactor.manual")}</p>
                  <div className="flex items-start gap-2 rounded bg-surface p-3">
                    <Mono className="min-w-0 flex-1 break-all text-xs">{phase.secret}</Mono>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(phase.secret);
                      }}
                    >
                      {t("twoFactor.copy")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Recovery codes section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-fg">{t("twoFactor.recovery")}</p>
                <p className="text-sm text-fg-subtle">{t("twoFactor.recoveryLine")}</p>
              </div>
              <div className="rounded-lg border border-warn/30 bg-warn/5 p-4">
                <div className="mb-3 flex items-start gap-2">
                  <svg className="mt-0.5 h-5 w-5 shrink-0 text-warn" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-fg">
                    <span className="font-medium">{t("twoFactor.important")}</span>{" "}
                    {t("twoFactor.importantLine")}
                  </p>
                </div>
                {/* One column until `sm`. A recovery code is `xxxx-xxxx-xxxx-xxxx`
                    — 19 monospace characters, about 176px at this size — and
                    half of a 375px screen minus the two nested paddings is
                    roughly 150. Two columns clipped the last block of every
                    code on a phone, which is the one screen where these are
                    unrecoverable if you copy them down wrong. */}
                <div className="grid grid-cols-1 gap-2 rounded bg-canvas p-4 sm:grid-cols-2">
                  {phase.recoveryCodes.map((recovery) => (
                    <code key={recovery} className="rounded bg-surface px-2 py-1.5 text-center font-mono text-sm tabular-nums">
                      {recovery}
                    </code>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="secondary" size="sm" onClick={() => void copyCodes()}>
                    {copied ? t("twoFactor.copied") : t("twoFactor.copyAll")}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={downloadCodes}>
                    {t("twoFactor.download")}
                  </Button>
                </div>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-3 rounded-lg bg-surface p-4">
                <input
                  type="checkbox"
                  checked={codesSaved}
                  onChange={(e) => setCodesSaved(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                />
                <span className="text-sm text-fg">{t("twoFactor.saved")}</span>
              </label>
            </div>

            {/* Verification form */}
            <form onSubmit={confirmEnrollment} className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-fg">{t("twoFactor.verifyTitle")}</p>
                <p className="text-sm text-fg-subtle">{t("twoFactor.verifyLine")}</p>
              </div>
              <Field label={t("twoFactor.code")} htmlFor="confirm-code">
                <Input
                  id="confirm-code"
                  inputMode="numeric"
                  placeholder="000000"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="text-center text-lg tracking-widest"
                />
              </Field>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={!codesSaved}
                  className="w-full sm:w-auto"
                >
                  {t("twoFactor.turnOn")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setPhase({ step: "idle" });
                    setPassword("");
                    setCode("");
                    setError(null);
                  }}
                >
                  {t("twoFactor.cancel")}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Success state */}
        {phase.step === "success" && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-mint/20">
              <svg className="h-8 w-8 text-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-fg">{t("twoFactor.success")}</p>
              <p className="text-sm text-fg-subtle">{t("twoFactor.successLine")}</p>
            </div>
          </div>
        )}

        {/* Disable phase */}
        {phase.step === "disable" && (
          <form onSubmit={disable} className="space-y-4">
            {error && <Alert>{error}</Alert>}
            <div className="rounded-lg bg-danger/10 p-4">
              <div className="flex items-start gap-3">
                <svg className="mt-0.5 h-5 w-5 shrink-0 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="space-y-1">
                  <p className="font-medium text-danger">{t("twoFactor.disableTitle")}</p>
                  <p className="text-sm text-fg-subtle">{t("twoFactor.disableLine")}</p>
                </div>
              </div>
            </div>
            <Field label={t("twoFactor.password")} htmlFor="disable-password">
              <Input
                id="disable-password"
                type="password"
                autoComplete="current-password"
                required
                autoFocus
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Field label={t("twoFactor.disableCode")} htmlFor="disable-code">
              <Input
                id="disable-code"
                inputMode="numeric"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="text-center text-lg tracking-widest"
              />
            </Field>
            <div className="flex gap-3">
              <Button type="submit" variant="danger" loading={submitting}>
                {t("twoFactor.disableAction")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setPhase({ step: "idle" });
                  setPassword("");
                  setCode("");
                  setError(null);
                }}
              >
                {t("twoFactor.cancel")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}
